"use client";

import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence, useInView } from "framer-motion";
import axios from "../../../../components/axios";

import TextAnimation from "../../../../components/TextAnimation";

let breakPoints = [
  { width: 1, itemsToShow: 2 },
  { width: 550, itemsToShow: 4, itemsToScroll: 2, pagination: false },
  { width: 850, itemsToShow: 4 },
  { width: 1150, itemsToShow: 4, itemsToScroll: 2 },
  { width: 1450, itemsToShow: 5 },
  { width: 1750, itemsToShow: 6 },
];

const DonorsPartnersSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [donors, setDonors] = useState([]);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { threshold: 0.2, once: true });

  const GetData = () => {
    axios
      .get("donors/getdonors")
      .then(({ data }) => {
        setDonors(data);
      })
      .catch((error) => {
        console.error("Error fetching donors:", error);
        // Fallback to static data if API fails
      });
  };

  useEffect(() => {
    GetData();
  }, []);

  useEffect(() => {
    if (isInView) {
      setIsVisible(true);
    }
  }, [isInView]);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <SectionContainer ref={sectionRef}>
      <ContentWrapper>
        <TextAnimation
          margin={[-20, 0]}
          opacity={[0, 1]}
          direction={"top"}
          delay={0}
        >
          <HeaderSection>
            <SectionTitle>Our Donors & Partners</SectionTitle>
            <SectionDescription>
              We collaborate with organizations that share our vision of
              empowering communities and creating sustainable change
            </SectionDescription>
          </HeaderSection>
        </TextAnimation>

        <PartnersGrid>
          {donors.map((donor, index) => (
            <motion.div
              key={donor.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
            >
              <PartnerCard onClick={() => toggleExpand(donor.id)}>
                <CardHeader>
                  <LogoContainer>
                    <PartnerLogo
                      src={donor.image}
                      alt={`${donor.title} logo`}
                    />
                  </LogoContainer>
                  <PartnerName>{donor.title}</PartnerName>
                </CardHeader>

                <DescriptionContainer
                  initial={false}
                  animate={{
                    height: expandedId === donor.id ? "auto" : 0,
                    opacity: expandedId === donor.id ? 1 : 0,
                  }}
                  transition={{
                    duration: 0.4,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                >
                  <PartnerDescription>{donor.description}</PartnerDescription>
                </DescriptionContainer>
              </PartnerCard>
            </motion.div>
          ))}
        </PartnersGrid>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <CollaborationMessage>
            <MessageText>
              Interested in becoming a partner?{" "}
              <MessageLink>Contact us</MessageLink> to explore collaboration
              opportunities.
            </MessageText>
          </CollaborationMessage>
        </motion.div>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default DonorsPartnersSection;

// Styled Components
const SectionContainer = styled.section`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 5rem 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 0;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2.5rem, 4vw, 3.5rem);
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 1rem 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const SectionDescription = styled.p`
  font-size: clamp(1.1rem, 1.5vw, 1.3rem);
  color: #64748b;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;

  /* @media (max-width: 1024px) {
    display: none;
  } */

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
    gap: 1rem;
  }
`;

const PartnerCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  height: ${(props) => (props.isExpanded ? "auto" : "100px")};

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
    border-color: #10b981;
  }
`;

const CardHeader = styled(motion.div)`
  display: flex;
  align-items: center;
  padding: 1rem;
  gap: 1rem;
  height: 100px;
`;

const LogoContainer = styled(motion.div)`
  width: 70px;
  height: 70px;
  border-radius: 12px;
  overflow: hidden;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
`;

const PartnerLogo = styled.img`
  width: 80%;
  height: 80%;
  object-fit: contain;
`;

const PartnerName = styled(motion.h3)`
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  line-height: 1.4;
  display: flex;
  flex-direction: column;

  .description {
    font-family: "sf-heavy";
    font-size: 1.5rem;
    color: #251f68;
    margin-bottom: 5px;
  }

  .title {
    font-family: "selawik-bold";
    font-size: 0.9rem;
    color: #696698;
  }
`;

const DescriptionContainer = styled(motion.div)`
  padding: 0 1rem 1.5rem;
  overflow: hidden;
`;

const PartnerDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: #64748b;
  margin: 0;
`;

const CollaborationMessage = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
  box-shadow: 0 4px 20px rgba(16, 185, 129, 0.2);
`;

const MessageText = styled.p`
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0;
`;

const MessageLink = styled.a`
  color: white;
  text-decoration: underline;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #f0fdf4;
  }
`;
